import { useCallback, useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Mic, Square, Trash2, Loader2, Send, AudioLines, MessageCircle } from "lucide-react";

const border = { borderColor: "var(--wofy-border)" };
const API_BASE = process.env.REACT_APP_BACKEND_URL;

function fmtTime(s) {
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

export default function VoiceMemories({ memorialKey, personName, user, endpoint }) {
  const base = endpoint || `/memorials/${memorialKey}/voice-memories`;
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | recording | preview | uploading
  const [seconds, setSeconds] = useState(0);
  const [blob, setBlob] = useState(null);
  const [name, setName] = useState(user?.name || "");
  const [relation, setRelation] = useState("");
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get(base);
      setItems(data.items || []);
    } catch { /* ignore */ }
    setLoaded(true);
  }, [base]);

  useEffect(() => { load(); }, [load]);

  // Poll while any transcript is still cooking.
  useEffect(() => {
    if (!items.some((i) => i.status === "transcribing")) return;
    const t = setInterval(load, 5000);
    const stop = setTimeout(() => clearInterval(t), 3 * 60 * 1000);
    return () => { clearInterval(t); clearTimeout(stop); };
  }, [items, load]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        setBlob(new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" }));
        setPhase("preview");
      };
      rec.start();
      recRef.current = rec;
      setSeconds(0);
      setPhase("recording");
      timerRef.current = setInterval(() => setSeconds((s) => {
        if (s >= 179) { try { recRef.current?.stop(); } catch { /* noop */ } clearInterval(timerRef.current); }
        return s + 1;
      }), 1000);
    } catch {
      toast.error("Microphone access was blocked. Allow the microphone and try again.");
    }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    try { recRef.current?.stop(); } catch { /* noop */ }
  };

  const discard = () => { setBlob(null); setSeconds(0); setPhase("idle"); };

  const submit = async () => {
    if (!name.trim()) { toast.error("Please add your name."); return; }
    if (!blob) return;
    setPhase("uploading");
    try {
      const fd = new FormData();
      const ext = (blob.type || "").includes("mp4") ? "mp4" : "webm";
      fd.append("audio", blob, `memory.${ext}`);
      fd.append("contributor_name", name.trim());
      fd.append("relation", relation.trim());
      await http.post(base, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Voice memory saved — transcribing now. The recording is preserved forever.");
      setBlob(null); setSeconds(0); setPhase("idle"); setRelation("");
      load();
    } catch (err) {
      setPhase("preview");
      toast.error(err?.response?.data?.detail || "Upload failed — please try again.");
    }
  };

  return (
    <div className="space-y-6" data-testid="voice-memories-section">
      <div className="wofy-card">
        <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>VOICE MEMORIES</div>
        <h3 className="font-display text-2xl">Speak a memory of {personName || "them"}</h3>
        <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
          Record up to 3 minutes in any language. We keep the original recording forever and add a written transcript so future generations can read it too.
        </p>

        <div className="mt-4">
          {phase === "idle" && (
            <button type="button" onClick={startRecording} className="wofy-btn-primary" data-testid="voice-record-btn">
              <Mic className="w-4 h-4" /> Record a voice memory
            </button>
          )}
          {phase === "recording" && (
            <div className="flex items-center gap-4 flex-wrap">
              <span className="inline-flex items-center gap-2 text-sm font-mono" data-testid="voice-recording-timer">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" /> Recording {fmtTime(seconds)} / 3:00
              </span>
              <button type="button" onClick={stopRecording} className="wofy-btn-primary" data-testid="voice-stop-btn">
                <Square className="w-4 h-4" /> Stop
              </button>
            </div>
          )}
          {(phase === "preview" || phase === "uploading") && blob && (
            <div className="space-y-3" data-testid="voice-preview-panel">
              <audio controls src={URL.createObjectURL(blob)} className="w-full max-w-md" data-testid="voice-preview-audio" />
              <div className="grid md:grid-cols-2 gap-3 max-w-xl">
                <label className="block">
                  <div className="text-[11px] font-mono uppercase tracking-wider mb-1">Your name *</div>
                  <input value={name} onChange={(e) => setName(e.target.value)} className="wofy-input" placeholder="e.g. Anita Menon" data-testid="voice-name-input" />
                </label>
                <label className="block">
                  <div className="text-[11px] font-mono uppercase tracking-wider mb-1">Relationship (optional)</div>
                  <input value={relation} onChange={(e) => setRelation(e.target.value)} className="wofy-input" placeholder="e.g. Granddaughter" data-testid="voice-relation-input" />
                </label>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={submit} disabled={phase === "uploading"} className="wofy-btn-primary disabled:opacity-60" data-testid="voice-submit-btn">
                  {phase === "uploading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {phase === "uploading" ? "Saving…" : "Save this memory"}
                </button>
                <button type="button" onClick={discard} disabled={phase === "uploading"} className="wofy-btn-ghost" data-testid="voice-discard-btn">
                  <Trash2 className="w-4 h-4" /> Discard
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {loaded && items.length > 0 && (
          <div className="overline" style={{ color: "var(--wofy-primary)" }}>RECORDED MEMORIES ({items.length})</div>
        )}
        {!loaded && <div className="text-sm opacity-60">Loading voice memories…</div>}
        {loaded && items.length === 0 && (
          <div className="rounded-xl border border-dashed p-6 text-center text-sm" style={border} data-testid="voice-empty-state">
            <AudioLines className="w-6 h-6 mx-auto mb-2 opacity-40" />
            No voice memories yet — be the first to speak one.
          </div>
        )}
        {items.map((it) => (
          <div key={it.id} className="rounded-xl border p-4" style={border} data-testid="voice-memory-item">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
              <div className="text-sm font-semibold">
                {it.contributor_name}
                {it.relation && <span className="font-normal opacity-70"> · {it.relation}</span>}
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`🎙️ Listen to ${it.contributor_name}'s voice memory of ${personName || "our family"}:\n${window.location.href}`)}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold text-white shadow-sm hover:shadow transition"
                  style={{ background: "#25D366" }}
                  data-testid="voice-share-whatsapp"
                  title="Share this voice memory on WhatsApp"
                >
                  <MessageCircle className="w-3 h-3" /> Share
                </a>
                <div className="text-[10px] font-mono opacity-60">{(it.created_at || "").slice(0, 10)}</div>
              </div>
            </div>
            <audio controls src={`${API_BASE}${it.audio_url}`} className="w-full max-w-md" preload="metadata" />
            {it.status === "transcribing" && (
              <p className="mt-2 text-xs italic inline-flex items-center gap-1.5" style={{ color: "var(--wofy-text-soft)" }} data-testid="voice-transcribing">
                <Loader2 className="w-3 h-3 animate-spin" /> Transcribing…
              </p>
            )}
            {it.status === "ready" && it.transcript && (
              <p className="mt-2 text-sm whitespace-pre-line" style={{ color: "var(--wofy-text-soft)" }} data-testid="voice-transcript">
                "{it.transcript}"
              </p>
            )}
            {it.status === "transcript_failed" && (
              <p className="mt-2 text-xs italic opacity-60" data-testid="voice-transcript-failed">{it.transcript_error}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
